import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { customerPlanSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import { Customer } from '../customers/model'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as customersActionCreators from '../customers/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import * as actionCreators from './actionCreators'
import * as actionTypes from './actionTypes'
import { CustomerPlan } from './model'

// Fetch

const fetchCustomerPlansEpic = (action$, store) => {
    const tableIdentifier = 'customer_plans_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `customer-plans/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(customerPlanSchema)
                        )

                        const customerPlans = normalizedEntitiesToRecordMap(
                            normalizedData.entities.customer_plans,
                            CustomerPlan,
                            normalizedData.result
                        )
                        const customers = normalizedEntitiesToRecordMap(normalizedData.entities.customers, Customer)

                        const observables = [
                            Observable.of(customersActionCreators.fetchCustomersFulfilled(customers)),
                            Observable.of(actionCreators.fetchCustomerPlansFulfilled(customerPlans)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCustomerPlansRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCustomerPlanEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().customerPlans.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `customer-plans/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, customerPlanSchema)

                        const customerPlans = normalizedEntitiesToRecordMap(normalizedData.entities.customer_plans, CustomerPlan)
                        const customers = normalizedEntitiesToRecordMap(normalizedData.entities.customers, Customer)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCustomerPlansFulfilled(customerPlans)),
                            Observable.of(actionCreators.fetchCustomerPlanFulfilled()),
                            Observable.of(customersActionCreators.fetchCustomersFulfilled(customers))
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCustomerPlansRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCustomerPlanEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().customerPlans.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = { ...action.payload.values }
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `customer-plans`
            let method = 'POST'

            if (values.id) {
                path = `customer-plans/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveCustomerPlanDuplicityFound(ajaxResponse.response.duplicity)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, customerPlanSchema)

                        const customerPlans = normalizedEntitiesToRecordMap(normalizedData.entities.customer_plans, CustomerPlan)
                        const customerPlan = customerPlans.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCustomerPlanFulfilled(customerPlan)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCustomerPlanRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCustomerPlanEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().customerPlans.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `customer-plans/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCustomerPlanFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCustomerPlanRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportCustomerPlansEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `customer-plans/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [fetchCustomerPlansEpic, fetchCustomerPlanEpic, saveCustomerPlanEpic, deleteCustomerPlanEpic, exportCustomerPlansEpic]
